import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, takeUntil } from 'rxjs';
import { ContextMenuItem, ContextMenuItemSelectedEvent } from '../../core/interfaces/context-menu.interface';
import { ContextMenuService } from '../../core/services/context-menu.service';

/**
 * Context Menu Component
 *
 * Displays a context menu with configurable items
 * Used internally by SmartGridComponent when contextMenu is enabled
 *
 * @template T - The type of row data
 */
@Component({
  selector: 'app-context-menu',
  standalone: true,
  imports: [
    CommonModule,
    MatMenuModule,
    MatIconModule,
    MatDividerModule,
    MatTooltipModule,
  ],
  template: `
    <div
      class="context-menu-container"
      [style.position]="'fixed'"
      [style.left.px]="position.x"
      [style.top.px]="position.y"
      [style.display]="isOpen ? 'block' : 'none'"
      [style.z-index]="9999">

      <div class="context-menu-backdrop" (click)="close()" *ngIf="isOpen"></div>

      <div class="context-menu" *ngIf="isOpen && data">
        <div
          *ngFor="let item of visibleItems; trackBy: trackByLabel"
          class="context-menu-item-wrapper">

          <!-- Divider -->
          <mat-divider *ngIf="item.divider" class="context-menu-divider"></mat-divider>

          <!-- Regular Item -->
          <button
            *ngIf="!item.divider"
            class="context-menu-item"
            [class.context-menu-item-disabled]="isItemDisabled(item)"
            [class.context-menu-item-primary]="item.color === 'primary'"
            [class.context-menu-item-accent]="item.color === 'accent'"
            [class.context-menu-item-warn]="item.color === 'warn'"
            [ngClass]="item.cssClass || ''"
            [disabled]="isItemDisabled(item)"
            [matTooltip]="item.tooltip || ''"
            (click)="onItemClick(item, $event)">

            <mat-icon *ngIf="item.icon" class="context-menu-item-icon">
              {{ item.icon }}
            </mat-icon>

            <span class="context-menu-item-label">
              {{ item.label }}
            </span>

            <span *ngIf="item.shortcut" class="context-menu-item-shortcut">
              {{ item.shortcut }}
            </span>
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .context-menu-container {
      pointer-events: none;
    }

    .context-menu-backdrop {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 9998;
      pointer-events: all;
      background: transparent;
    }

    .context-menu {
      pointer-events: all;
      position: relative;
      z-index: 9999;
      min-width: 200px;
      max-width: 320px;
      background: white;
      border-radius: 4px;
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
      padding: 8px 0;
      overflow: hidden;

      :host-context(.dark) & {
        background: #1f2937;
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.6);
      }
    }

    .context-menu-item-wrapper {
      display: block;
    }

    .context-menu-divider {
      margin: 8px 0;

      :host-context(.dark) & {
        border-top-color: #374151;
      }
    }

    .context-menu-item {
      display: flex;
      align-items: center;
      width: 100%;
      padding: 10px 16px;
      border: none;
      background: transparent;
      color: #374151;
      font-size: 14px;
      text-align: left;
      cursor: pointer;
      transition: background-color 0.2s ease;
      gap: 12px;

      &:hover:not(:disabled) {
        background-color: #f3f4f6;
      }

      &:active:not(:disabled) {
        background-color: #e5e7eb;
      }

      :host-context(.dark) & {
        color: #d1d5db;

        &:hover:not(:disabled) {
          background-color: #374151;
        }

        &:active:not(:disabled) {
          background-color: #4b5563;
        }
      }
    }

    .context-menu-item-disabled {
      opacity: 0.5;
      cursor: not-allowed;

      &:hover {
        background-color: transparent !important;
      }
    }

    .context-menu-item-primary {
      color: #2563eb;

      :host-context(.dark) & {
        color: #60a5fa;
      }
    }

    .context-menu-item-accent {
      color: #7c3aed;

      :host-context(.dark) & {
        color: #a78bfa;
      }
    }

    .context-menu-item-warn {
      color: #dc2626;

      :host-context(.dark) & {
        color: #f87171;
      }
    }

    .context-menu-item-icon {
      font-size: 20px;
      width: 20px;
      height: 20px;
      flex-shrink: 0;
    }

    .context-menu-item-label {
      flex: 1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .context-menu-item-shortcut {
      font-size: 12px;
      opacity: 0.6;
      margin-left: auto;
      padding-left: 16px;
      flex-shrink: 0;
    }
  `],
})
export class ContextMenuComponent<T> implements OnInit, OnDestroy {
  /**
   * Menu items
   */
  @Input() items: ContextMenuItem<T>[] = [];

  /**
   * Row data
   */
  @Input() data: T | null = null;

  /**
   * Row index
   */
  @Input() rowIndex: number = -1;

  /**
   * Menu position
   */
  @Input() position: { x: number; y: number } = { x: 0, y: 0 };

  /**
   * Is the menu open?
   */
  @Input() isOpen: boolean = false;

  /**
   * Event emitted when an item is selected
   */
  @Output() itemSelected = new EventEmitter<ContextMenuItemSelectedEvent<T>>();

  /**
   * Event emitted when menu is closed
   */
  @Output() menuClosed = new EventEmitter<void>();

  /**
   * Destroy subject
   */
  private destroy$ = new Subject<void>();

  /**
   * Visible items (excluding hidden)
   */
  public visibleItems: ContextMenuItem<T>[] = [];

  constructor(private contextMenuService: ContextMenuService<T>) {}

  ngOnInit(): void {
    // Subscribe to service state for global management
    this.contextMenuService.state$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.items = state.items;
        this.data = state.data;
        this.rowIndex = state.rowIndex;
        this.position = state.position;
        this.isOpen = state.isOpen;

        if (this.data) {
          this.updateVisibleItems();
        }
      });

    // Listen for clicks outside to close menu
    if (typeof window !== 'undefined') {
      window.addEventListener('click', this.handleClickOutside);
      window.addEventListener('contextmenu', this.handleClickOutside);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (typeof window !== 'undefined') {
      window.removeEventListener('click', this.handleClickOutside);
      window.removeEventListener('contextmenu', this.handleClickOutside);
    }
  }

  /**
   * Update visible items based on hidden property
   */
  private updateVisibleItems(): void {
    if (!this.data) {
      this.visibleItems = [];
      return;
    }

    this.visibleItems = this.contextMenuService.getVisibleItems(this.items, this.data);
  }

  /**
   * Check if item is disabled
   */
  public isItemDisabled(item: ContextMenuItem<T>): boolean {
    if (!this.data) return true;
    return this.contextMenuService.isItemDisabled(item, this.data);
  }

  /**
   * Handle item click
   */
  public onItemClick(item: ContextMenuItem<T>, event: MouseEvent): void {
    if (this.isItemDisabled(item) || !this.data) {
      return;
    }

    // Execute the item action
    if (item.action) {
      item.action(this.data, event);
    }

    // Emit event
    this.itemSelected.emit({
      item,
      data: this.data,
      rowIndex: this.rowIndex,
    });

    // Close the menu
    this.close();
  }

  /**
   * Close the context menu
   */
  public close(): void {
    this.contextMenuService.close();
    this.menuClosed.emit();
  }

  /**
   * Handle clicks outside the menu
   */
  private handleClickOutside = (event: Event): void => {
    // Close menu when clicking anywhere
    if (this.isOpen) {
      this.close();
    }
  };

  /**
   * Track by function for ngFor
   */
  public trackByLabel(index: number, item: ContextMenuItem<T>): any {
    return item.label || index;
  }
}
